import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, output } from '@angular/core';
import { BOOKING_PAGE_VIEWS, BOOKING_REDIRECTION_VIEWS, GTM_EVENTS, WIDGET_TEMPLATES_CONST } from '@constants/app.constants';
import { environment } from '@environments/environment';
import { BookedAppointment } from '@models/appointment-booking.model';
import { CurrentViewData } from '@models/widget-conf.model';
import { WidgetTemplateModel } from '@models/widget-template.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { BookingService } from '@services/feature/booking.service';
import { WidgetService } from '@services/feature/widget.service';
import { FileService } from '@services/utils/file.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { LoggerService } from '@services/utils/logger.service';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-booking-success-view',
  templateUrl: './booking-success-view.component.html',
  styleUrls: ['./booking-success-view.component.scss'],
  imports: [CswTextComponent, ButtonComponent, TranslateModule, CalioTranslationPipe]
})
export class BookingSuccessViewComponent implements OnInit {

  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private translate = inject(TranslateService);
  private fileService = inject(FileService);
  protected bookingService = inject(BookingService);
  protected widgetService = inject(WidgetService);

  protected readonly bookingRedirectionViews = BOOKING_REDIRECTION_VIEWS;
  protected lang: string;
  protected successTemplate: WidgetTemplateModel;
  protected successTitleTemplate: WidgetTemplateModel;
  protected redirectionButtonTemplate: WidgetTemplateModel;
  protected manageBookingTemplate: WidgetTemplateModel;
  protected downloadIcsTemplate: WidgetTemplateModel;
  // Snapshot of the appointments booked by the flow that just completed — read at mount
  // (the success view is mount-on-active in both normal and one-page modes).
  protected appointments: BookedAppointment[] = [];
  // ICS generation happens on the backend and can take a moment — track in-flight
  // downloads per appointment so the link shows a spinner and ignores double-clicks.
  protected downloadingIcsUuids = new Set<string>();

  nextPageEvent = output<CurrentViewData>();
  previousPageEvent = output<CurrentViewData>();

  constructor() {
    this.lang = this.translate.getFallbackLang();
    this.translate.onLangChange.subscribe(language => this.lang = language.lang);
  }

  ngOnInit(): void {
    this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_success);
    this.appointments = this.bookingService.lastBookedAppointments;
    this.setupTemplates();
  }

  private setupTemplates(): void {
    this.successTemplate = this.bookingService.widgetTemplates.find(template =>
      template.identifier === WIDGET_TEMPLATES_CONST.WIDGET_BOOKING_INFO_APPOINTMENT
    );
    this.successTemplate && (this.successTemplate.is_multi_language = 1);

    this.successTitleTemplate = this.bookingService.widgetTemplates.find(template =>
      template.identifier === WIDGET_TEMPLATES_CONST.WIDGET_APPOINTMENT_SUCCESS_TITLE
    );
    this.successTitleTemplate && (this.successTitleTemplate.is_multi_language = 1);

    this.redirectionButtonTemplate = this.bookingService.widgetTemplates.find(template =>
      template.identifier === WIDGET_TEMPLATES_CONST.REDIRECTION_BUTTON_TEMPLATE
    );
    this.redirectionButtonTemplate && (this.redirectionButtonTemplate.is_multi_language = 1);

    this.manageBookingTemplate = this.bookingService.widgetTemplates.find(template =>
      template.identifier === WIDGET_TEMPLATES_CONST.WIDGET_MANAGE_BOOKING_LABEL
    );
    this.manageBookingTemplate && (this.manageBookingTemplate.is_multi_language = 1);

    this.downloadIcsTemplate = this.bookingService.widgetTemplates.find(template =>
      template.identifier === WIDGET_TEMPLATES_CONST.WIDGET_DOWNLOAD_APPOINTMENT_ICS_FILE
    );
    this.downloadIcsTemplate && (this.downloadIcsTemplate.is_multi_language = 1);
  }

  protected showAppointmentLink(): boolean {
    return this.bookingService.showAppointmentLinkOnSuccess();
  }

  protected showIcsDownload(): boolean {
    return this.bookingService.showIcsDownloadOnSuccess();
  }

  protected appointmentLink(uuid: string): string {
    return `${environment.calioDashboardBaseUrl}/appointments/${uuid}`;
  }

  protected isDownloadingIcs(uuid: string): boolean {
    return this.downloadingIcsUuids.has(uuid);
  }

  protected downloadIcs(uuid: string): void {
    if (this.downloadingIcsUuids.has(uuid)) {
      return;
    }
    this.downloadingIcsUuids.add(uuid);
    this.bookingService.downloadAppointmentIcs(uuid)
      .pipe(finalize(() => this.downloadingIcsUuids.delete(uuid)))
      .subscribe({
        next: (result: { file: string, type: string }) => {
          this.fileService.saveFile(
            environment.hostName + result.file,
            this.fileService.fetchFilenameFromPath(result.file)
          );
        },
        error: (error: HttpErrorResponse) => LoggerService.error(error)
      });
  }

  protected onAdditionalBooking(): void {
    this.bookingService.gtmStartTriggered = false;
    this.googleAnalyticsService.generateSessionUuid();
    if (this.bookingService.isBeforeQuestionsMode()) {
      this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.BOOKING_QUESTIONS_VIEW_BEFORE, isUserInteracted: true });
    } else {
      if (this.bookingService.isMeetingTypeStepInFlow()) {
        this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW, isUserInteracted: true });
      } else {
        this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.STORE_VIEW, isUserInteracted: true });
      }
    }
  }

  protected redirectTo(): void {
    this.bookingService.redirectToSuccessPage();
  }
}
